import { Alert, Box, Divider, Link, Paper, Stack, Typography } from '@mui/material';
import CheckIcon from '@mui/icons-material/CheckCircleOutline';
import AppConfig from './AppConfig';
import React, { ReactNode, useContext, useEffect } from 'react';
import { Link as RouterLink, useLocation, useNavigate } from 'react-router';
import UserContext from './UserContext';
import { rememberSelectedPlan } from './business/Plans';

type Mode = 'login' | 'sign-in' | 'sign-up' | 'connect';

function XeroButton(props: { href: string; label: string }) {
  // xero-sso.js replaces this element with Xero's branded button.
  return <span data-xero-sso data-href={props.href} data-label={props.label} />;
}

function AuthCard(props: { title: string; subtitle: ReactNode; notice?: ReactNode; children: ReactNode }) {
  return (
    <Box sx={{ display: 'flex', justifyContent: 'center', pt: { xs: 3, sm: 8 }, px: 2 }}>
      <Paper variant="outlined" sx={{ width: '100%', maxWidth: 440, p: { xs: 3, sm: 4 }, borderRadius: 3, textAlign: 'center' }}>
        {props.notice && <Box sx={{ mb: 3, textAlign: 'left' }}>{props.notice}</Box>}
        <Typography component="h1" sx={{ fontSize: '1.5rem', fontWeight: 700, color: '#007ca3' }}>
          {props.title}
        </Typography>
        <Typography color="text.secondary" sx={{ mt: 1, mb: 3 }}>
          {props.subtitle}
        </Typography>
        {props.children}
      </Paper>
    </Box>
  );
}

const SIGN_UP_POINTS = [
  'See every client’s BAS readiness on one dashboard',
  'Spot unreconciled transactions and PAYGW, wages and super mismatches',
  'Send reminder emails to clients in a couple of clicks',
];

export default function XeroSso(props: { mode: Mode; redirectIfLoggedIn?: boolean; notice?: ReactNode }) {
  const navigate = useNavigate();
  const userContext = useContext(UserContext);
  const { mode, redirectIfLoggedIn = true, notice } = props;
  const search = useLocation().search;
  const reason = new URLSearchParams(search).get('reason');
  const loggedIn = userContext.user != null;

  useEffect(() => {
    if (!document.getElementById('xeroSso')) {
      const script = document.createElement('script');
      script.src = 'https://edge.xero.com/platform/sso/xero-sso.js';
      script.id = 'xeroSso';
      document.body.appendChild(script);
    }
  });

  // Keep the plan picked on the website for the Subscription page, after signing in with Xero
  useEffect(() => rememberSelectedPlan(search), [search]);

  useEffect(() => {
    if (loggedIn && redirectIfLoggedIn) {
      navigate('/dashboard');
    }
  }, [loggedIn, redirectIfLoggedIn]);

  if (mode === 'connect') {
    return <XeroButton href={AppConfig.oauthConnectionsPath} label="Connect organisations from Xero" />;
  }

  if (mode === 'sign-up') {
    return (
      <AuthCard
        title="Create your BAS Ready account"
        subtitle="Sign up with the Xero login you use for your practice."
        notice={reason === 'noUser' && <Alert severity="info">We couldn't find a BAS Ready account for that Xero login. Sign up below to create one.</Alert>}
      >
        <Stack component="ul" gap={1} sx={{ listStyle: 'none', p: 0, m: 0, mb: 3, textAlign: 'left' }}>
          {SIGN_UP_POINTS.map(point => (
            <Stack component="li" key={point} direction="row" gap={1} alignItems="flex-start">
              <CheckIcon sx={{ color: '#1e6b3a', fontSize: 20, mt: '2px' }} />
              <Typography variant="body2">{point}</Typography>
            </Stack>
          ))}
        </Stack>
        <XeroButton href={AppConfig.oauthSignupPath} label="Sign up using Xero" />
        <Divider sx={{ my: 3 }} />
        <Typography variant="body2">
          Already have an account?{' '}
          <Link component={RouterLink} to="/login">
            Sign in
          </Link>
        </Typography>
      </AuthCard>
    );
  }

  return (
    <AuthCard title="Sign in to BAS Ready" subtitle="Use the Xero login you use for your practice." notice={notice}>
      <XeroButton href={AppConfig.oauthLoginPath} label="Sign in with Xero" />
      <Divider sx={{ my: 3 }} />
      <Typography variant="body2">
        New to BAS Ready?{' '}
        <Link href={AppConfig.oauthSignupPath} data-testid="signup-link">
          Create an account
        </Link>
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
        Need a hand?{' '}
        <Link component={RouterLink} to="/support">
          Contact support
        </Link>
      </Typography>
    </AuthCard>
  );
}
